import jsPDF from 'jspdf';
import controls from './controls';
import { helpers } from './reportingHelpers';

export default function ReportContext (mvc, parentContext) {
    this.mvc = mvc;
    mvc.reportContext = this;                                       //Track this context on the view being rendered for communication back and forth...EK
    this.reportName = mvc.drivingView.Definition.Name;

    // NOTE: We will always work with a clone copy of the report definition because we add run-time properties that we don't want to accidently capture...EK
    this.layout = JSON.parse(JSON.stringify(mvc.drivingView.Definition.ReportLayout));

    this.prepareDataContext();

    if (parentContext == null){
        this.rootContext = this;

        // Create the pdf control using the page settings defined in the report layout...EK
        let pdfOptions = {
            orientation: this.layout.PageSettings.Orientation,
            format: this.layout.PageSettings.PaperSize,
            unit: this.layout.PageSettings.Units,
            hotfixes: ['px_scaling'],            // Here, we apply the hotfix for px scaling since the core has the incorrect scale factor...EK
            compressPdf: true
        };
        this.pdf = new jsPDF(pdfOptions);
        // Note: sub context (parentContext != null) do not have a pointer to a pdf.  It is always the root context that has the pdf reference...EK

        //Default font / size / etc...EK
        this.pdf.setFont('Arial', 'normal');
        this.pdf.setFontSize(9);        //Note: Default to 9pt font for PDF (not pixels - PDF is always in pt not px)...EK

        //TODO: When we do a page break (either manual or automatic), we need to calculate the pageSize, and bounds below since orientation and size may change across breaks...EK
        this.pageSize = {
            width: this.pdf.internal.pageSize.width,
            height: this.pdf.internal.pageSize.height
        };

        // Calculate the print boundaries using the margin defined...EK
        this.printBounds = {
            left: parseInt(this.layout.PageSettings.Margin.Left),
            top: parseInt(this.layout.PageSettings.Margin.Top),
            right: this.pageSize.width - parseInt(this.layout.PageSettings.Margin.Right),
            bottom: this.pageSize.height - parseInt(this.layout.PageSettings.Margin.Top) - parseInt(this.layout.PageSettings.Margin.Bottom)
        };

        // Calculate the detail boundaries using the above sections and the height of the detail section...EK
        this.detailBounds = {
            left: this.printBounds.left,
            top: this.printBounds.top
            + (this.layout.Sections.Header == null ? 0 : this.layout.Sections.Header.Height)
            + (this.layout.Sections.PageHeader == null ? 0 : this.layout.Sections.PageHeader.Height),
            right: this.printBounds.right,
            bottom: this.printBounds.bottom
            - (this.layout.Sections.Footer == null ? 0 : this.layout.Sections.Footer.Height)
            - (this.layout.Sections.PageFooter == null ? 0 : this.layout.Sections.PageFooter.Height)
        };

        // Holds reference to the current pointer for location (within the print bounds)...EK
        this.ptr = {
            top: 0,
            left: 0
        };
        //Note: If there is a parent context, there is no pointer since we always use the root...EK
    }
    else {
        // Capture the root context of this report.  This is used for root page controls (footers, finalization, etc.)
        let ckContext = parentContext;
        while (ckContext.parentContext != null){
            ckContext = ckContext.parentContext;
        }
        this.rootContext = ckContext;

        // Copy the print boundaries from the parent (owning context) ... EK
        this.printBounds = {
            left: parentContext.printBounds.left,
            top: parentContext.printBounds.top,
            right: parentContext.printBounds.right,
            bottom: parentContext.printBounds.bottom
        };

        this.detailBounds = {
            left: parentContext.detailBounds.left,     // Note: this will be modified when used as a sub-context based on the left location of the sub report control...EK
            top: parentContext.detailBounds.top,
            right: parentContext.detailBounds.right,
            bottom: parentContext.detailBounds.bottom
        };
    }

    // This holds the control that is actively being rendered...EK
    this.renderingControl = null;

    // When true, we have just broken by a page break control (only flagged when breaking mid detail section).  This will trigger different logic in rendering of sections / paging...EK
    this.activePageBreak = false;

    // Array that holds all page number controls and the page they need to be rendered on...EK
    this.pageLabels = [];

    // This flag is set and checked to know if content requires a new page to continue to be rendered...EK
    this.newPageNeeded = false;

    //Holds a reference to the parent report context when rendering a sub-report...EK
    this.parentContext = parentContext;

    // Placeholder for when we render sub-views so we have a recursive object hier for rendering the report...EK
    this.subContexts = [];

    if (parentContext != null){
        parentContext.subContexts.push(this);       // Add myself to the parent context subContexts array so it can be reused and cleaned up...EK
    }

    // Capture report event hooks (if present) ...EK
    let definedEventHooks = app.dv.reporting.globalSettings.eventHandlers.getVal(mvc.drivingView.UID);
    this.hooks = {
        onReportStart: (definedEventHooks != null ? definedEventHooks.onReportStart : null),
        onNewPage: (definedEventHooks != null ? definedEventHooks.onNewPage : null),
        onDetailStart: (definedEventHooks != null ? definedEventHooks.onDetailStart : null),
        onDetailPreRender: (definedEventHooks != null ? definedEventHooks.onDetailPreRender : null),
        onDetailFinish: (definedEventHooks != null ? definedEventHooks.onDetailFinish : null),
        onReportFinish: (definedEventHooks != null ? definedEventHooks.onReportFinish : null)
    };

    // Placeholder for if we encounter an error, this is a single place where the error is logged and passed up...EK
    this.error = null;
}

ReportContext.prototype.controls = controls;

/**
 * Parses data records on the mvc for use by the report (includes groupings).
 */
ReportContext.prototype.prepareDataContext = function() {
    if (this.layout.Sections.Groups && this.layout.Sections.Groups.any()) {
        // This is a grouped report, build the group structure...EK

        let groupBy = (records, groupDef) => {
            //Extract the values from the grid Record list provided (based on the grouped property)...EK
            let recordValues = records.select(sr => sr.gridValues.getVal(groupDef.PropertyID));
            //Calculate the distinct values from the complete list...EK
            let distinctValues = recordValues.map(v => v.getValue()).distinct();

            let groups = [];
            distinctValues.forEach((distinctValue) => {
                groups.push({
                    propertyID: groupDef.PropertyID,
                    key: distinctValue,
                    header: groupDef.Header,
                    footer: groupDef.Footer,
                    data: recordValues.filter(v => v.getValue() === distinctValue).map(v => v.parentRecord)
                });
            });

            return groups;
        };

        let finalGrouping = null;
        let lastGrouping = null;

        // Loop through each group definition, calculate the group and store a hierarchy of groups/records
        this.layout.Sections.Groups.forEach((groupDef) => {
            if (finalGrouping == null) {
                finalGrouping = lastGrouping = groupBy(this.mvc.gridController.getSortedRecords(false), groupDef);
            }
            else {
                lastGrouping.forEach((lastGroup) => {
                    lastGroup.subGroups = groupBy(lastGroup.data, groupDef);
                });

                lastGrouping = lastGrouping.selectMany(g => g.subGroups);
            }
        });

        this.runTimeGroups = finalGrouping;
    }
    else {
        this.dataContextRecords = this.mvc.gridController.getSortedRecords(false);
    }

    this.dataContextIndex = 0;

    //Use the first record in the grid controller (since it controls ordering).  Use this to get the gridRecord object...EK
    if (this.mvc.selectedRecord == null) {
        this.mvc.gridController.selectFirst();   // If we have no prior selection, select the first record...EK
    }

    let gcRecord = this.mvc.selectedRecord;

    if (gcRecord == null) {
        if (app.dv.reporting.globalSettings.createDummyRecordWhenEmpty) {
            // We have a blank report and the setting says to create a dummy record.  Create this record and set the context to it...EK
            this.dataContext = app.dv.mvc.createRecord(this.mvc, '');
        }
        else {
            this.dataContext = null;
        }
    }
    else {
        //We have a context record, set the context...EK
        this.dataContext = gcRecord;
    }
};

ReportContext.prototype.setDataContext = async function () {
    let ctx = this,
        mvcSetPromises = [];

    if (ctx.mvc.selectedRecord !== ctx.dataContext) {
        //Set the selected record (we're bypassing the mvc setSelectedRecord function because it doesn't support waiting until the set has been pushed to all listeners...EK
        ctx.mvc.selectedRecord = ctx.dataContext;
        ctx.mvc.gridController.setSelectedRecord(ctx.dataContext);

        this.mvc.listeners.forEach((m) => {
            mvcSetPromises.push(new Promise((resolve) => {
                m.suppressSaveConfirmation = true;
                app.dv.mvc.setLinkedRecord(m, ctx.dataContext, false, function (setMvc) {
                    setMvc.suppressSaveConfirmation = false;
                    resolve();
                });
            }));
        });

        await Promise.all(mvcSetPromises);
    }
};

/**
 * Fires when a report is initialized, but no processing has taken place yet.
 * @returns {Promise}
 */
ReportContext.prototype.onReportStart = function(){
    if (this.hooks.onReportStart != null){
        return this.hooks.onReportStart(this) || Promise.resolve();
    }
    else {
        return Promise.resolve();
    }
};

/**
 * Fires when a new page is created during report execution (including the first page after report start).
 * @returns {Promise}
 */
ReportContext.prototype.onNewPage = function() {
    if (this.hooks.onNewPage != null){
        return this.hooks.onNewPage(this) || Promise.resolve();
    }
    else {
        return Promise.resolve();
    }
};

/**
 * Fires when the detail section is initializing (potentially for a new data context record).
 * @returns {Promise}
 */
ReportContext.prototype.onDetailStart = function() {
    if (this.hooks.onDetailStart != null){
        return this.hooks.onDetailStart(this) || Promise.resolve();
    }
    else {
        return Promise.resolve();
    }
};

/**
 * Fires when the detail section has been pre-rendered (grown if needed) and is ready to render to the PDF.
 * @returns {Promise}
 */
ReportContext.prototype.onDetailPreRender = function() {
    if (this.hooks.onDetailPreRender != null){
        return this.hooks.onDetailPreRender(this) || Promise.resolve();
    }
    else {
        return Promise.resolve();
    }
};

/**
 * Fires after a detail has been rendered to the PDF.
 * @returns {Promise}
 */
ReportContext.prototype.onDetailFinish = function() {
    if (this.hooks.onDetailFinish != null){
        return this.hooks.onDetailFinish(this) || Promise.resolve();
    }
    else {
        return Promise.resolve();
    }
};

/**
 * Fires when the report has finished rendering all sections for all detail records.
 * @returns {Promise}
 */
ReportContext.prototype.onReportFinish = function(){
    if (this.hooks.onReportFinish != null){
        return this.hooks.onReportFinish(this) || Promise.resolve();
    }
    else {
        return Promise.resolve();
    }
};

/**
 * Renders the report header section of the report.  This section is ran only once at the beginning of a report.
 * @param callback
 */
ReportContext.prototype.renderReportHeader = function(callback){
    if (this.layout.Sections.Header == null || this.layout.Sections.Header.Height === 0){
        callback();
        return;
    }
    //Else: we have header content to process...EK

    let ctx = this;
    ctx.renderingDetail = false;
    ctx.preRenderSection(ctx.layout.Sections.Header, () => {
        ctx.renderControls(ctx.layout.Sections.Header, () => {
            ctx.rootContext.ptr.top += ctx.layout.Sections.Header.runTime.height;       // Update our pointer for the current page so the content is always relative to its section...EK
            callback();
        });
    });
};

/**
 * This renders the page header section of the report.  This section is run once per page of the report.
 * @param callback
 */
ReportContext.prototype.renderPageHeader = function(callback){
    if (this.layout.Sections.PageHeader == null || this.layout.Sections.PageHeader.Height === 0){
        callback();
        return;
    }
    //Else: we have header content to process...EK

    let ctx = this;
    ctx.renderingDetail = false;
    ctx.preRenderSection(ctx.layout.Sections.PageHeader, () => {
        // We need to check to see if this header will go past our print area, if so get a new page and then render...EK
        if (ctx.rootContext.ptr.top + ctx.layout.Sections.PageHeader.runTime.height > ctx.rootContext.detailBounds.bottom) {
            ctx.rootContext.renderPageFooter(() => {
                ctx.rootContext.makeNewPage(false, () => {
                    ctx.renderControls(ctx.layout.Sections.PageHeader, () => {
                        ctx.rootContext.ptr.top += ctx.layout.Sections.PageHeader.runTime.height;       // Update our pointer for the current page so the content is always relative to its section...EK
                        callback();
                    });
                });
            });
        }
        else {
            ctx.renderControls(ctx.layout.Sections.PageHeader, () => {
                ctx.rootContext.ptr.top += ctx.layout.Sections.PageHeader.runTime.height;       // Update our pointer for the current page so the content is always relative to its section...EK
                callback();
            });
        }
    });
};

/**
 * This renders all page headers for a new page (including sub-contexts).
 * @returns {Promise}
 */
ReportContext.prototype.renderAllPageHeaders = async function() {
    let ctx = this;

    if (ctx.parentContext == null){
        // Just run this root context header and resolve...EK
        await new Promise((resolve) => {
            ctx.renderPageHeader(resolve);
        });
    }
    else {
        // We need to capture the hierarchy of contexts to the root...EK
        let contexts = [ctx];
        let tempContext = ctx;
        while (tempContext.parentContext != null) {
            contexts.push(tempContext.parentContext);
            tempContext = tempContext.parentContext;
        }

        // Reverse the order so they render top -> down on the page...EK
        contexts.reverse();

        // Render each page header context in (now reversed) order...EK
        for(let x = 0; x < contexts.length; x++) {
            await new Promise((resolve) => {
                contexts[x].renderPageHeader(resolve);
            });
        }
    }
};

/**
 * Renders a grouped detail for a report (renders all headers, detail and footers for all groups).
 * @param groups - A runtime group object created in prepareDataContext.
 * @returns {Promise<void>}
 */
ReportContext.prototype.renderGroupedDetail = async function(groups) {
    let ctx =  this;

    for (let g = 0; g < groups.length; g++) {
        let currentGroup = groups[g];

        // Set the data context for the header to the first in this group...EK
        ctx.dataContext = currentGroup.data.first();

        await ctx.setDataContext();

        if (currentGroup.header != null) {
            ctx.renderingDetail = false;
            await ctx.renderGroupSection(currentGroup.header);
        }

        if (currentGroup.subGroups != null) {
            // This group has sub groupings, these must be evaluated in hierarchical order (recursive call) ... EK
            await ctx.renderGroupedDetail(currentGroup.subGroups);
        }
        else {
            await new Promise((resolve, reject) => {
                ctx.dataContextRecords = currentGroup.data;
                ctx.dataContextIndex = 0;
                ctx.renderDetail(() => {
                    resolve();
                })
            });

            if (ctx.newPageNeeded) {
                await new Promise((resolve, reject) => {
                    ctx.rootContext.renderPageFooter(() => {
                        ctx.makeNewPage(false, async () => {
                            await ctx.renderAllPageHeaders();
                            resolve();
                        });
                    });
                });
            }

        }

        if (currentGroup.footer != null) {
            // Render the footer for this group.  NOTE: the dataContext will be the last record in the group...EK
            await ctx.renderGroupSection(currentGroup.footer);
        }
    }

    ctx.cleanEndPage();
};

ReportContext.prototype.cleanEndPage = function () {
    let ctx = this;

    if (ctx.activePageBreak) {
        // We have a page break at the end of the report, we will have a blank page at the end.  Truncate it...EK
        if (!ctx.newPageNeeded) {
            ctx.rootContext.pdf.deletePage(ctx.rootContext.pdf.internal.pages.length - 1);
            if (ctx.pageLabels.any()) {
                ctx.pageLabels.pop();       //We need to remove this last pages label (it will be re-generated in the page footer render once this detail calls back) ... EK
            }
        }
        else {
            ctx.newPageNeeded = false;      //Since we are out of context, we don't want to create a new page (in the callback below)...EK
        }
    }
};

/**
 * This renders the detail section of the report.  This section is ran for each data context record in the view.
 * @param callback
 */
ReportContext.prototype.renderDetail = function(callback){
    // The detail assumes that the dataContext has already been set in the ctx variable based on initialization...EK
    //The detail render will assume that the page header is complete and will process the detail.
    //  If the detail runs to the next page, it will call page footer and then render page header and then continue where it left off for the detail section...EK

    if (this.layout.Sections.Detail == null || this.layout.Sections.Detail.Height === 0){
        callback();
        return;
    }
    //Else: we have footer content to process...EK

    let ctx = this;

    ctx.renderingDetail = true;
    const recordCodeFromContext = (ctx) => {
        return ctx.dataContext && ctx.dataContext.clientRecord && ctx.dataContext.clientRecord.Record && ctx.dataContext.clientRecord.Record.Code ?
            ctx.dataContext.clientRecord.Record.Code :
            '[NO CONTEXT]';
    };

    console.info(`${ctx.reportName} is rendering detail for ${recordCodeFromContext(ctx)}.`);
    ctx.onDetailStart().then(() => {
        ctx.preRenderSection(ctx.layout.Sections.Detail, () => {
            ctx.onDetailPreRender().then(() => {
                ctx.renderControls(ctx.layout.Sections.Detail, checkNext);
            });
        });
    });

    function checkNext() {
        //check to see if there was an error, if so we need to stop rendering and callback...EK
        if (ctx.error != null){
            console.log('Encountered an error rendering detail.  No more detail will process.');
            callback();
            return;
        }
        //Else: we continue processing detail...EK
        console.info(`${ctx.reportName} is finished rendering detail for ${recordCodeFromContext(ctx)}.`);

        ctx.onDetailFinish().then(() => {
            //Update the pointer (check to see if we can fit on the current page) ...EK
            let checkExtraBreak = false;
            if (ctx.activePageBreak){
                //If the last control in a detail section is a page break, we need to zero-out the runTime height (Otherwise the detail on this page will be offset)...EK
                checkExtraBreak = true;
            }
            else {
                // We push the pointer to the end of the last detail height...EK
                ctx.rootContext.ptr.top += ctx.layout.Sections.Detail.runTime.height;
            }

            //Get the next data context record...EK
            let lastContext = ctx.dataContext;
            ctx.dataContextIndex += 1;
            ctx.dataContext = ctx.dataContextRecords[ctx.dataContextIndex];

            console.info(`${ctx.reportName} is rendering detail for ${recordCodeFromContext(ctx)}.`);
            if (ctx.dataContext != null) {
                ctx.onDetailStart().then(() => {
                    ctx.setDataContext().then(() => {
                        // Check to see if this record will fit on the current page.  If not, we need a new page...EK
                        if (ctx.newPageNeeded === true){    // Before we render this, we need to check to see if we already know if we need a new page, if so, call back so that page can be created....EK
                            callback();
                            return;
                        }

                        setTimeout(() => {          //Note: the set timeout here is used to ensure a flat stack (stack overflow is possible here) ... EK
                            ctx.preRenderSection(ctx.layout.Sections.Detail, () => {
                                ctx.onDetailPreRender().then(() => {
                                    if (ctx.newPageNeeded === false
                                        && ctx.rootContext.ptr.top + ctx.layout.Sections.Detail.runTime.height <= ctx.detailBounds.bottom) {
                                        // It will fit, go ahead a render for this data context record...EK
                                        ctx.renderControls(ctx.layout.Sections.Detail, checkNext);
                                    }
                                    else {
                                        //Note: If this entire detail won't fit on a page, render controls
                                        ctx.newPageNeeded = true;
                                        callback();
                                    }
                                });
                            });
                        }, 0);
                    });
                });
            }
            else {
                // We are out of context, we are done rendering detail...EK
                // We need to reset the dataContext to the last context for any footer content that may depend on it...EK
                ctx.dataContext = lastContext;

                if (checkExtraBreak) {
                    ctx.cleanEndPage();
                }

                //We need to set the pointer to the top of the page footer (so it renders in the right place if the detail doesn't take all the detail bounds area)...EK
                callback();     //Will trigger the next section (page footer) ... EK
            }
        });
    }
};

/**
 * This renders the page footer section of the report.  Runs ones per page of the report at the bottom of the report.
 * @param callback
 */
ReportContext.prototype.renderPageFooter = function(callback){
    if (this.rootContext.layout.Sections.PageFooter == null || this.rootContext.layout.Sections.PageFooter.Height === 0){
        callback();
        return;
    }
    //Else: we have footer content to process...EK

    let ctx = this;
    ctx.renderingDetail = false;
    ctx.rootContext.ptr.top = ctx.rootContext.detailBounds.bottom;      // Move the pointer to the top of where the footer should go...EK
    ctx.preRenderSection(ctx.rootContext.layout.Sections.PageFooter, () => {
        ctx.renderControls(ctx.rootContext.layout.Sections.PageFooter, () => {
            ctx.rootContext.ptr.top += ctx.rootContext.layout.Sections.PageFooter.runTime.height;       // Update our pointer for the current page so the content is always relative to its section...EK
            callback();
        });
    });
};

/**
 * This renders the report footer section of the report.  Only runs ones per report.
 * @param callback
 */
ReportContext.prototype.renderReportFooter = function(callback){
    if (this.layout.Sections.Footer == null || this.layout.Sections.Footer.Height === 0){
        callback();
        return;
    }
    //Else: we have footer content to process...EK

    let ctx = this;
    ctx.renderingDetail = false;
    ctx.preRenderSection(ctx.layout.Sections.Footer, () => {
        ctx.renderControls(ctx.layout.Sections.Footer, () => {
            ctx.rootContext.ptr.top += ctx.layout.Sections.Footer.runTime.height;       // Update our pointer for the current page so the content is always relative to its section...EK
            callback();
        });
    });
};

/**
 * Renders a group header or footer section.
 * @param groupSection
 * @returns {Promise<any>}
 */
ReportContext.prototype.renderGroupSection = async function(groupSection) {
    let ctx = this;

    return new Promise((resolve, reject) => {
        ctx.preRenderSection(groupSection, async () => {
            // if (ctx.rootContext.ptr.top + groupSection.runTime.height > ctx.rootContext.detailBounds.bottom) {
            //     await new Promise((resolve1, reject1) => {
            //         // Rendering this section will go beyond the detail boundaries, a new page is needed...EK
            //         ctx.rootContext.renderPageFooter(() => {
            //             ctx.makeNewPage(false, async () => {
            //                 await ctx.renderAllPageHeaders();
            //                 resolve1();
            //             });
            //         });
            //     });
            // }

            ctx.renderControls(groupSection, () => {
                // Update our pointer for the current page so the content is always relative to its section...EK
                ctx.rootContext.ptr.top += groupSection.runTime.height;
                resolve();
            });
        });
    })
};

/**
 * This renders a page of the report.  Ran Once per page and controls the rendering of the page header, detail and page footer sections of the report.
 * @param callback
 */
ReportContext.prototype.renderPage = function(callback) {
    let ctx = this;
    ctx.renderAllPageHeaders().then(() => {
        if (ctx.runTimeGroups != null) {
            ctx.renderGroupedDetail(ctx.runTimeGroups).then(() => {
                ctx.renderPageFooter(() => {
                    if (ctx.newPageNeeded === false){
                        callback();
                    }
                    else {
                        //We need a new page.. use a set timeout so we don't have a large call stack (and possible stack overflow)...EK
                        ctx.makeNewPage(true, callback);
                    }
                });
            });
        }
        else {
            ctx.renderDetail(() => {
                ctx.renderPageFooter(() => {
                    if (ctx.newPageNeeded === false){
                        callback();
                    }
                    else {
                        //We need a new page.. use a set timeout so we don't have a large call stack (and possible stack overflow)...EK
                        ctx.makeNewPage(true, callback);
                    }
                });
            });
        }
    });
};

/**
 * Creates a new PDF page to render to.
 * @param autoRenderNext - When true, the new page will trigger a new renderPage call (to render the page on natural breaks).
 * @param callback - function to call after the new page is available.
 */
ReportContext.prototype.makeNewPage = function(autoRenderNext, callback){
    let ctx = this;
    ctx.rootContext.pdf.addPage();
    ctx.rootContext.ptr.top = 0;

    ctx.newPageNeeded = false;

    ctx.rootContext.onNewPage().then(() => {
        if (autoRenderNext){            // This should automatically render the next page by default behavior...EK
            ctx.renderPage(callback);
        }
        else {
            callback();
        }
    });
};

/**
 * Performs a pre-render operation to size and adjust the section based on the content that will be rendered.
 * @param section - The current section to pre-render.
 * @param callback
 */
ReportContext.prototype.preRenderSection = function(section, callback){
    //This function will handle all sizing and expression evaluation for the section specified...EK
    if (section.runTime == null) {
        section.runTime = {
            height: section.Height
        };
    }
    else {
        section.runTime.height = section.Height;    // Reset here in case of growth...EK
    }

    let ctx = this;
    let ctlIndex = 0;
    let ctl = null;

    function preRenderCtl(){
        ctl = section.Controls[ctlIndex++];

        if (ctl == null){
            // We are done with the pre-render.  We need to account for control growth...EK

            // First, determine if we have a tableRow, if so, we need to adjust it based on all cells...EK
            let tableRowCtl = section.Controls.find(ctl => ctl.Type === 'tableRow');
            if (tableRowCtl != null) {
                if (section === ctx.layout.Sections.PageHeader && tableRowCtl.runTime.height > tableRowCtl.Height) {
                    // Add the amount this header grew...EK
                    section.runTime.height += (tableRowCtl.runTime.height - tableRowCtl.Height);
                }
                else if (section === ctx.layout.Sections.Detail) {
                    // the height of this section is the same as the grid...EK
                    section.runTime.height = tableRowCtl.runTime.height;
                }
            }

            // Recursively search all controls in this section, calculate any growth that causes a layout change, apply the layout updates...EK
            if (section === ctx.layout.Sections.Detail) {        // We only update the growth for the detail section...EK
                ctx.handleControlGrowth(section);
            }

            callback();
        }
        else {
            try {
                ctx.controls[ctl.Type].preRender(ctl, ctx, preRenderCtl);
            }
            catch (e){
                console.error(`Error pre-rendering type, check to see that you have a pre-render defined. Detail: ${e}`);
                callback();
            }
        }
    }

    if (!section.Controls.any()) {
        callback();
    }
    else {
        preRenderCtl();
    }
};

ReportContext.prototype.handleControlGrowth = function(section){
    /**
     * Search all controls to see if they have grown, if so we record it, if not we keep searching (search is recursive) ... EK
     * Note: This function also flattens the control group hierarchy to search for collisions...EK
     * @param controlGroup - The control group (or section) to search for growth...EK
     * @param controlsFound - The array of found control groups that have growth...EK
     * @param controlGroupList - The flattened array of control groups (to simplify collision detection) ... EK
     */
    function searchForGrowth(controlGroup, controlsFound, controlGroupList){
        for (let c = 0, cl = controlGroup.Controls.length; c < cl; c++){
            let ctl = controlGroup.Controls[c];
            if (ctl.Controls != null){
                controlGroupList.push(ctl);
                searchForGrowth(ctl, controlsFound, controlGroupList);
            }
            else if (ctl.Type === 'subReport' || ctl.Type === 'pageBreak'){
                controlGroupList.push(ctl);
            }
            else {
                if (ctl.runTime.grown) {        // If we find any controls that have grown, we switch out and just record the group...EK
                    // Add the growing control group (if it's not already in the list) ... EK
                    if (controlsFound.indexOf(controlGroup) === -1) { controlsFound.push(controlGroup); }

                    let heightAffect = ctl.runTime.height - ctl.Height; // Calculate the total growth of this control...EK
                    // We need to find all controls within in this control group that need to be moved...EK
                    let ctlsAffected = controlGroup.Controls.where((ctlToCheck) => {
                        return ctlToCheck.Top > ctl.Top + ctl.Height;
                    });

                    // Apply the height of the grown control to all controls below it...EK
                    if (ctlsAffected.any()) {
                        ctlsAffected.forEach((ctlToUpdate) => {
                            ctlToUpdate.runTime.top += heightAffect;
                        })
                    }

                    controlGroup.runTime.totalGrowth += heightAffect;       // Capture the total grown of this control group to apply to affected control groups...EK
                    controlGroup.runTime.height += heightAffect;
                }
            }
        }
    }

    let growthGroups = [],
        flattenedGroups = [];

    searchForGrowth(section, growthGroups, flattenedGroups);

    // Apply any growth found to control groups that are directly affected...EK
    if (growthGroups.length !== 0){
        growthGroups.forEach((growthGroup) => {
            // We need to check to see what controls will be impacted by this growth...EK
            //      Groups of controls that have a top coordinate > This growing control group top coordinate...EK
            flattenedGroups.forEach((ctlGroup) => {
                if (ctlGroup.runTime.top > growthGroup.runTime.top              // It's below the growing group...EK
                    && growthGroup.Left <= ctlGroup.Left){                      // It's within the width of the growing group...EK
                    // This ctl group is affected by this growth.. Apply to the group and it's controls...EK
                    ctlGroup.runTime.top += growthGroup.runTime.totalGrowth;
                    if (ctlGroup.Controls != null){
                        ctlGroup.Controls.forEach((ctl) => {
                            if (ctl.Controls == null) {     // We only apply the growth to non-control group controls...EK
                                ctl.runTime.top += growthGroup.runTime.totalGrowth;
                            }
                        });
                    }
                }
            });

            // Apply the growth to the section (since it must grow too) ... EK
            section.runTime.height += growthGroup.runTime.totalGrowth;
        });
    }
    // Else, no controls grew in this section, just return...EK
};

/**
 * Renders all controls in a report section.
 * @param section - The current report section to render.
 * @param callback
 */
ReportContext.prototype.renderControls = function(section, callback) {
    // NOTE: Only one control can be rendered at a time (no parallel rendering is allowed due to page break and sub reports) ...EK

    let ctl = null;
    let ctlInx = 0;
    let ctx = this;

    if (section.Controls.any() == false){
        callback();
    }
    else {
        renderControl();
    }

    function renderControl(){
        ctl = section.Controls[ctlInx++];

        if (ctl == null){
            //We are done rendering all controls in this section...EK
            callback();
        }
        else {
            let footerSection = false,
                headerSection = false;
            if (ctl.Type === 'pageBreak' && ctx.layout.Sections.Groups) {
                footerSection = ctx.layout.Sections.Groups.any(group => group.Footer === section);
                headerSection = ctx.layout.Sections.Groups.any(group => group.Header === section);
            }
            // To protect the designer, pageBreaks if placed in a section other than detail or group footer will not render...WR
            if (ctl.Type === 'pageBreak' && section !== ctx.layout.Sections.Detail && !footerSection && ! headerSection) {
                //This is not allowed, record to the console and skip rendering this control...EK
                console.log(`${ctl.Type} is not allowed in any section except for Detail.  Control will not be rendered.`);
                renderControl();
            }
            // To protect the designer, subReports if placed in a section other than detail will not render...EK
            if (ctl.Type === 'subReport' && section !== ctx.layout.Sections.Detail) {
                //This is not allowed, record to the console and skip rendering this control...EK
                console.log(`${ctl.Type} is not allowed in any section except for Detail.  Control will not be rendered.`);
                renderControl();
            }
            else {
                ctx.activePageBreak = false;            // Make sure this flag is reset before each control that's rendered (it's handled in the section render when needed) ... EK
                ctx.renderingControl = ctl;
                //We have a control to render, attempt to get the internal render controller...EK
                let pdfCtl = ctx.controls[ctl.Type];
                if (pdfCtl == null){
                    console.warn('No render defined for type: ' + ctl.Type);
                    renderControl();
                }
                else{
                    try {
                        pdfCtl.render(ctl, ctx, renderControl);
                    }
                    catch (e){
                        console.error(`Error occurred while rendering report: ${e}`);
                        renderControl();
                    }
                }
            }
        }
    }
};

/**
 * This function finalizes the report (including page numbering handling...EK
 * @param callback
 */
ReportContext.prototype.finalizeReport = function(callback){
    if (this.pageLabels.any() == false) {
        callback();
        return;
    }

    let pageLabelsIndex = 0;
    let ctx = this;

    function finished() {
        pageLabelsIndex++;
        if (pageLabelsIndex < ctx.pageLabels.length) {
            renderPageLabel();
        }
        else {
            callback();
        }
    }

    let totalPages = ctx.rootContext.pdf.internal.pages.length - 1;

    function renderPageLabel() {
        let pageLabel = ctx.pageLabels[pageLabelsIndex];

        ctx.rootContext.pdf.setPage(pageLabel.page);

        //Evaluate the expression for the page information.  Replace ##PageNumber and ##PageCount and then write it...EK
        let pageLabelText = pageLabel.labelDef.Expression;
        pageLabelText = pageLabelText.replace(/##PageNumber/gi, pageLabel.page.toString());
        pageLabelText = pageLabelText.replace(/##PageCount/gi, totalPages.toString());
        pageLabelText = app.dv.tools.evaluateExpression(pageLabelText, ctx.mvc) || pageLabelText;

        let s = pageLabel.labelDef.Style;
        if (pageLabel.labelDef.runTime == null) {
            pageLabel.labelDef.runTime = {
                top: pageLabel.labelDef.Top,
                height: pageLabel.labelDef.Height
            };
        }
        else {
            pageLabel.labelDef.runTime.top = pageLabel.labelDef.Top;
            pageLabel.labelDef.runTime.height = pageLabel.labelDef.Height;
        }

        pageLabel.labelDef.runTime.textMetrics = helpers.fitText(s.fontName, s.fontSize, s.fontWeight, pageLabelText, pageLabel.labelDef.Width, s.padding);

        //Set the pointer to the capture point and render the text on the page..EK
        ctx.rootContext.ptr.left = pageLabel.leftPtr;
        ctx.rootContext.ptr.top = pageLabel.topPtr;

        ctx.controls.label.render(pageLabel.labelDef, ctx, finished);
    }

    renderPageLabel();      //Kick off the process...EK
};

/**
 * When called it will destroy object references to facilitate memory cleanup.  It will recursively clean up sub contexts.
 */
ReportContext.prototype.cleanup = function(){
    if (this.isDisposed === true) return;

    this.isDisposed = true;
    this.pdf = null;
    this.hooks = null;

    if (this.mvc != null){
        if (this.mvc.loadedForReport){
            // We should close this report that was opened in the background (fire and forget)...EK
            app.dv.actionLibrary.inlineExe(this.mvc, app.dv.actionLibrary.actionStores.dvCore.CloseView, { SuppressDirtyCheck: true });
        }
        else {
            // We need to (attempt to) set this view record to the first record.
            if (!(this.mvc.isClosing || this.mvc.isDisposed)) {
                //Only select the first records if there are no listeners.
                if (this.mvc.listeners && this.mvc.listeners.length ===0) {
                    this.mvc.gridController.selectFirst();
                }

            }
        }

        this.mvc.reportContext = null;
        this.mvc = null;
    }

    this.parentContext = null;

    this.subContexts.forEach((subCtx) => {
        subCtx.cleanup();
    });

    this.dataContextRecords = [];
    this.runTimeGroups = null;
    this.subContexts = [];
};
